import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { SectionTitle } from "@/components/GlassBits";
import { formatDate } from "@/lib/format";
import { Plus, X, Trash2 } from "lucide-react";

export default function Websites() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ website_name: "", domain: "", callback_url: "", allowed_ips: "" });

  const load = async () => setItems((await api.get("/admin/websites")).data);
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/admin/websites", {
        website_name: form.website_name,
        domain: form.domain,
        callback_url: form.callback_url || null,
        allowed_ips: form.allowed_ips.split(",").map(s => s.trim()).filter(Boolean),
      });
      toast.success("Website registered");
      setOpen(false);
      setForm({ website_name: "", domain: "", callback_url: "", allowed_ips: "" });
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };

  const del = async (id) => {
    if (!window.confirm("Delete website?")) return;
    await api.delete(`/admin/websites/${id}`);
    toast.success("Deleted"); load();
  };

  return (
    <div>
      <SectionTitle eyebrow="Merchants" title="Websites" right={<button onClick={() => setOpen(true)} className="ldc-btn-primary px-4 py-2 rounded-md text-sm inline-flex items-center gap-2" data-testid="web-new"><Plus size={14}/> New website</button>} />
      <div className="grid gap-3">
        {items.length === 0 && <div className="ldc-glass p-8 text-center text-white/50">No websites connected.</div>}
        {items.map((w) => (
          <div key={w.id} className="ldc-glass p-5" data-testid={`web-row-${w.id}`}>
            <div className="flex items-start justify-between gap-4 flex-wrap">
              <div>
                <div className="font-display text-lg">{w.website_name}</div>
                <div className="text-xs text-white/50">{w.domain}</div>
                {w.callback_url && <div className="text-xs text-white/40 mt-1">Callback: <span className="text-[#fbce18]/80">{w.callback_url}</span></div>}
                <div className="mt-2 text-xs">
                  <div><span className="text-white/40">API key:</span> <code className="font-mono text-[#fbce18]">{w.api_key}</code></div>
                  <div><span className="text-white/40">Secret:</span> <code className="font-mono text-white/70">{w.secret}</code></div>
                  {w.allowed_ips?.length > 0 && <div className="mt-1"><span className="text-white/40">IPs:</span> {w.allowed_ips.join(", ")}</div>}
                </div>
                <div className="text-[10px] text-white/30 mt-2">Created {formatDate(w.created_at)}</div>
              </div>
              <button onClick={() => del(w.id)} className="p-2 rounded hover:bg-white/5 text-white/60 hover:text-red-400 transition-colors" data-testid={`web-del-${w.id}`}><Trash2 size={16}/></button>
            </div>
          </div>
        ))}
      </div>

      {open && (
        <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setOpen(false)}>
          <form onSubmit={submit} className="ldc-glass p-6 w-full max-w-md" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-display text-xl">New Website</h3>
              <button type="button" onClick={() => setOpen(false)} className="p-1.5 rounded hover:bg-white/5"><X size={18}/></button>
            </div>
            <div className="space-y-3">
              <F label="Website name" v={form.website_name} on={(v) => setForm({...form, website_name: v})} testid="web-input-name" required />
              <F label="Domain" v={form.domain} on={(v) => setForm({...form, domain: v})} testid="web-input-domain" required />
              <F label="Callback URL" v={form.callback_url} on={(v) => setForm({...form, callback_url: v})} testid="web-input-callback" />
              <F label="Allowed IPs (comma separated)" v={form.allowed_ips} on={(v) => setForm({...form, allowed_ips: v})} testid="web-input-ips" />
            </div>
            <div className="flex justify-end gap-2 mt-5">
              <button type="button" onClick={() => setOpen(false)} className="px-4 py-2 rounded-md border border-white/10 text-sm">Cancel</button>
              <button type="submit" className="ldc-btn-primary px-5 py-2 rounded-md text-sm" data-testid="web-submit">Create</button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
function F({ label, v, on, testid, required }) {
  return (
    <label className="block">
      <div className="text-[10px] tracking-[0.24em] uppercase text-white/40 mb-1.5">{label}</div>
      <input value={v} onChange={(e) => on(e.target.value)} required={required} className="ldc-input w-full rounded-md px-3 py-2 text-sm" data-testid={testid} />
    </label>
  );
}
